import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ResponsiveContainer, BarChart, Bar, Tooltip as RTooltip } from 'recharts';
import { BarChart3, Clock, LogIn, Zap, Users, AlertTriangle, Eye, MessageSquare, CalendarDays, Briefcase, Flame, Info, Target, Save, Loader2 } from 'lucide-react';
import { WorkHoursChart, fmtMins } from '../components/WorkHoursChart';

const fmtDur = (s) => {
  if (!s) return '—';
  if (s < 60) return '<1m';
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const timeAgo = (iso) => {
  if (!iso) return 'never';
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return 'just now';
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
};

const isOnline = (iso) => iso && (Date.now() - new Date(iso).getTime()) < 180000;

const ROLE_STYLE = {
  admin: 'text-blue-400 border-blue-500/30 bg-blue-500/10',
  ac_member: 'text-purple-400 border-purple-500/30 bg-purple-500/10',
  analyst: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10',
};

function TotalCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-card border border-border rounded-sm p-4 flex items-center gap-3" data-testid={testId}>
      <div className="w-9 h-9 rounded-sm bg-blue-500/10 flex items-center justify-center shrink-0">
        <Icon className="w-4.5 h-4.5 text-blue-400" style={{ width: 18, height: 18 }} />
      </div>
      <div className="min-w-0">
        <p className="text-xl font-heading text-foreground leading-none">{value}</p>
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">{label}</p>
      </div>
    </div>
  );
}

function TimeStat({ label, seconds }) {
  return (
    <div className="flex-1 bg-secondary/50 border border-border/60 rounded-sm px-2.5 py-1.5 text-center">
      <p className="text-sm font-mono text-foreground">{fmtDur(seconds)}</p>
      <p className="text-[9px] text-muted-foreground uppercase tracking-wider">{label}</p>
    </div>
  );
}

function CountStat({ icon: Icon, value, label, title }) {
  return (
    <div className="flex items-center gap-1.5" title={title}>
      <Icon className="w-3 h-3 text-muted-foreground/60" />
      <span className="text-xs font-mono text-foreground">{value}</span>
      <span className="text-[10px] text-muted-foreground">{label}</span>
    </div>
  );
}

function HoursVisibility({ u, allUsers, onReload }) {
  const [saving, setSaving] = useState('');
  const current = u.hours_visible_to || [];
  const others = (allUsers || []).filter(x => x.id !== u.id && x.role !== 'ac_member' && x.status !== 'pending');
  const toggle = async (viewerId) => {
    const next = current.includes(viewerId) ? current.filter(x => x !== viewerId) : [...current, viewerId];
    setSaving(viewerId);
    try {
      await api.setUserHoursVisibility(u.id, next);
      const who = others.find(x => x.id === viewerId)?.name?.split(' ')[0] || 'Nutzer';
      toast.success(next.includes(viewerId) ? `${who} sieht jetzt die Stunden von ${u.name?.split(' ')[0]}` : `${who} sieht die Stunden von ${u.name?.split(' ')[0]} nicht mehr`);
      if (onReload) await onReload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setSaving('');
  };
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`hours-visibility-${u.id}`}
      title="Nur die gestempelten Stunden (Monat, Wochen, Stempelzeiten) — keine Aktivitäten, Deals oder Notizen">
      <span className="text-[9px] text-muted-foreground/70 uppercase tracking-wider flex items-center gap-1"><Eye className="w-2.5 h-2.5" /> Stunden sichtbar für</span>
      {others.map(x => {
        const on = current.includes(x.id);
        return (
          <button key={x.id} onClick={(e) => { e.stopPropagation(); toggle(x.id); }} disabled={!!saving} data-testid={`hours-visibility-${u.id}-${x.id}`}
            className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border transition-colors disabled:opacity-50 ${on ? 'border-emerald-500/50 text-emerald-300 bg-emerald-500/10' : 'border-border text-muted-foreground hover:text-foreground'}`}>
            {saving === x.id ? <Loader2 className="w-3 h-3 animate-spin inline" /> : (x.name?.split(' ')[0] || x.email)}
          </button>
        );
      })}
      {!others.length && <span className="text-[10px] text-muted-foreground">—</span>}
    </div>
  );
}

// Admin sets a weekly hour target; when set, the member can clock in/out and we show their hours here.
function TimeTargetSection({ u, onReload, allUsers }) {
  const clock = u.clock;
  const hasTarget = (u.weekly_target_hours || 0) > 0;
  const [value, setValue] = useState(u.weekly_target_hours ? String(u.weekly_target_hours) : '');
  const [saving, setSaving] = useState(false);
  const stop = (e) => e.stopPropagation();

  useEffect(() => { setValue(u.weekly_target_hours ? String(u.weekly_target_hours) : ''); }, [u.weekly_target_hours]);

  const save = async (e) => {
    stop(e);
    const hours = parseFloat((value || '0').replace(',', '.'));
    if (isNaN(hours) || hours < 0) { toast.error('Ungültiger Stundenwert'); return; }
    setSaving(true);
    try {
      await api.setUserWeeklyTarget(u.id, hours);
      toast.success(hours > 0 ? `Wochenziel für ${u.name?.split(' ')[0] || 'Nutzer'}: ${hours}h` : `Zeiterfassung für ${u.name?.split(' ')[0] || 'Nutzer'} deaktiviert`);
      if (onReload) await onReload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setSaving(false);
  };

  const weekPct = clock?.target_minutes ? Math.min(100, Math.round((clock.week_minutes / clock.target_minutes) * 100)) : 0;
  const changed = (value || '') !== (u.weekly_target_hours ? String(u.weekly_target_hours) : '');

  return (
    <div className="pt-2.5 mt-1 border-t border-border/50 space-y-2.5" onClick={stop} data-testid={`time-section-${u.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[9px] text-muted-foreground/70 uppercase tracking-wider flex items-center gap-1"><Target className="w-2.5 h-2.5" /> Wochenziel</span>
        <div className="flex items-center gap-1">
          <input type="number" min="0" step="0.5" value={value} onChange={(e) => setValue(e.target.value)} onClick={stop}
            placeholder="z. B. 15" data-testid={`time-target-input-${u.id}`}
            className="w-20 bg-secondary/60 border border-border rounded-sm px-2 py-1 text-xs text-foreground font-mono focus:outline-none focus:border-blue-500/50" />
          <span className="text-[10px] text-muted-foreground">h</span>
          <button onClick={save} disabled={saving || !changed} data-testid={`time-target-save-${u.id}`}
            className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded-sm border border-blue-500/40 text-blue-300 hover:bg-blue-500/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Speichern
          </button>
        </div>
        {u.clocked_in_since && (
          <span className="ml-auto flex items-center gap-1 text-[10px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5" data-testid={`clocked-in-${u.id}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> eingestempelt
          </span>
        )}
      </div>

      {hasTarget && clock && (
        <>
          <div className="flex gap-2">
            <div className="flex-1 bg-secondary/50 border border-border/60 rounded-sm px-2.5 py-1.5 text-center" data-testid={`clock-week-${u.id}`}>
              <p className="text-sm font-mono text-foreground">{fmtMins(clock.week_minutes)}</p>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Diese Woche · {weekPct}%</p>
            </div>
            <div className="flex-1 bg-secondary/50 border border-border/60 rounded-sm px-2.5 py-1.5 text-center" data-testid={`clock-month-${u.id}`}>
              <p className="text-sm font-mono text-foreground">{fmtMins(clock.month_minutes)}</p>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Dieser Monat</p>
            </div>
            <div className="flex-1 bg-secondary/50 border border-border/60 rounded-sm px-2.5 py-1.5 text-center">
              <p className="text-sm font-mono text-amber-400">{u.weekly_target_hours}h</p>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Ziel / Woche</p>
            </div>
          </div>
          <div data-testid={`clock-chart-${u.id}`}>
            <WorkHoursChart weeks={clock.weeks || []} targetHours={u.weekly_target_hours} height={130} />
          </div>
          <HoursVisibility u={u} allUsers={allUsers} onReload={onReload} />
        </>
      )}
    </div>
  );
}

function UserCard({ u, onOpen, onReload, allUsers }) {
  const online = isOnline(u.last_seen_at);
  const bd = u.breakdown_30d || {};
  return (
    <div onClick={() => onOpen(u)} title="Klicken für 24h-Profil & kompletten Log"
      className="bg-card border border-border rounded-sm p-4 space-y-3 cursor-pointer hover:border-blue-500/40 transition-colors"
      data-testid={`analytics-user-card-${u.id}`}>
      {/* Header */}
      <div className="flex items-center gap-2.5 flex-wrap">
        <div className="relative shrink-0">
          <div className="w-9 h-9 rounded-full bg-blue-600/20 flex items-center justify-center text-blue-400 text-sm font-medium font-mono">
            {u.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          {online && <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-card animate-pulse" data-testid={`online-dot-${u.id}`} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm text-foreground font-medium truncate">{u.name}</p>
            <span className={`text-[9px] px-1.5 py-0.5 rounded border font-mono uppercase ${ROLE_STYLE[u.role] || ROLE_STYLE.analyst}`}>
              {u.role === 'ac_member' ? 'AC' : u.role}
            </span>
            {u.status && u.status !== 'approved' && (
              <span className="text-[9px] px-1.5 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-400 font-mono uppercase">{u.status}</span>
            )}
          </div>
          <p className="text-[10px] text-muted-foreground truncate">{u.email}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Last login</p>
          <p className="text-xs font-mono text-foreground" data-testid={`last-login-${u.id}`}>{timeAgo(u.last_login_at)}</p>
        </div>
      </div>

      {/* Active time */}
      <div>
        <p className="text-[9px] text-muted-foreground/70 uppercase tracking-wider mb-1 flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> Time in app</p>
        <div className="flex gap-2">
          <TimeStat label="Today" seconds={u.time_today_s} />
          <TimeStat label="7 days" seconds={u.time_7d_s} />
          <TimeStat label="30 days" seconds={u.time_30d_s} />
          <TimeStat label="All time" seconds={u.time_all_s} />
        </div>
      </div>

      {/* Sparkline: 30d actions */}
      <div>
        <div className="flex items-center justify-between mb-0.5">
          <p className="text-[9px] text-muted-foreground/70 uppercase tracking-wider flex items-center gap-1"><Zap className="w-2.5 h-2.5" /> Actions — last 30 days</p>
          <p className="text-[10px] font-mono text-muted-foreground">{u.actions_7d} <span className="text-muted-foreground/50">(7d)</span> · {u.actions_30d} <span className="text-muted-foreground/50">(30d)</span> · {u.actions_all} <span className="text-muted-foreground/50">(all)</span></p>
        </div>
        <div className="h-10">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={u.actions_by_day} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
              <RTooltip cursor={{ fill: 'rgba(59,130,246,0.08)' }}
                content={({ active, payload }) => active && payload?.length ? (
                  <div className="bg-popover border border-border rounded-sm px-2 py-1 text-[10px] text-foreground font-mono">
                    {payload[0].payload.date}: {payload[0].value} action{payload[0].value !== 1 ? 's' : ''}
                  </div>
                ) : null} />
              <Bar dataKey="count" fill="#3b82f6" radius={[1, 1, 0, 0]} minPointSize={1} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Counters */}
      <div className="flex items-center gap-x-4 gap-y-1.5 flex-wrap pt-1 border-t border-border/50">
        <CountStat icon={LogIn} value={u.logins_7d} label="logins (7d)" title={`${u.logins_30d} logins in 30 days · ${u.logins_all} all time`} />
        <CountStat icon={Flame} value={u.active_days_all} label="active days" title="Days with at least one minute in the app (since tracking started)" />
        <CountStat icon={Briefcase} value={u.claimed_deals} label="claimed" title="Currently claimed deals" />
        <CountStat icon={Eye} value={u.viewed_deals} label="viewed" title="Deals opened (all time)" />
        <CountStat icon={MessageSquare} value={bd.notes || 0} label="notes (30d)" title={`${u.comments_total} comments all time`} />
        <CountStat icon={CalendarDays} value={bd.meetings || 0} label="meetings (30d)" title="Meeting agenda adds + logged calls" />
        {u.pending_decisions > 0 && (
          <span className="flex items-center gap-1 text-[10px] text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5" data-testid={`pending-decisions-${u.id}`}>
            <AlertTriangle className="w-2.5 h-2.5" /> {u.pending_decisions} decision{u.pending_decisions > 1 ? 's' : ''} pending
          </span>
        )}
      </div>

      <TimeTargetSection u={u} onReload={onReload} allUsers={allUsers} />
    </div>
  );
}

function UserDetailDialog({ user, onClose, onResetDone }) {
  const [detail, setDetail] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    setDetail(null); setConfirmReset(false);
    if (!user) return;
    api.getUserActivityDetail(user.id, { limit: 100 })
      .then(res => setDetail(res.data))
      .catch(() => toast.error('Detail konnte nicht geladen werden'));
  }, [user]);

  const loadMore = async () => {
    if (!detail?.next_before) return;
    setLoadingMore(true);
    try {
      const res = await api.getUserActivityDetail(user.id, { before: detail.next_before, limit: 100 });
      setDetail(d => ({ ...d, log: [...d.log, ...res.data.log], has_more: res.data.has_more, next_before: res.data.next_before }));
    } catch { toast.error('Laden fehlgeschlagen'); }
    finally { setLoadingMore(false); }
  };

  const doReset = async () => {
    try {
      const res = await api.resetUserPresence(user.id);
      toast.success(`Zeiterfassung zurückgesetzt (${res.data.deleted_days} Tage gelöscht)`);
      setConfirmReset(false);
      onResetDone();
    } catch { toast.error('Reset fehlgeschlagen'); }
  };

  const hourly = detail?.hourly_24h || [];
  const maxActive = Math.max(...hourly.map(h => h.active_s), 300);
  const fmtTs = (iso) => iso ? new Date(iso).toLocaleString('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <Dialog open={!!user} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-card border-border rounded-sm max-w-2xl max-h-[85vh] flex flex-col" data-testid="user-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-foreground flex items-center justify-between gap-2 flex-wrap pr-6">
            <span className="flex items-center gap-2"><Clock className="w-5 h-5 text-blue-400" /> {user?.name}</span>
            {!confirmReset ? (
              <button onClick={() => setConfirmReset(true)} data-testid="reset-presence-btn"
                className="text-[11px] font-mono px-2 py-1 rounded-sm border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors">
                Zeit zurücksetzen
              </button>
            ) : (
              <span className="flex items-center gap-2 text-[11px] font-mono">
                <span className="text-red-400">Alle erfassten Stunden von {user?.name} löschen?</span>
                <button onClick={doReset} data-testid="reset-presence-confirm-btn"
                  className="px-2 py-1 rounded-sm bg-red-600 text-white hover:bg-red-500 transition-colors">Ja, löschen</button>
                <button onClick={() => setConfirmReset(false)} data-testid="reset-presence-cancel-btn"
                  className="px-2 py-1 rounded-sm border border-border text-muted-foreground">Abbrechen</button>
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        {!detail ? (
          <div className="flex items-center justify-center py-10"><span className="w-5 h-5 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" /></div>
        ) : (
          <div className="space-y-4 overflow-hidden flex flex-col min-h-0">
            <div>
              <p className="text-[10px] text-muted-foreground/70 uppercase tracking-wider mb-1.5">Letzte 24 Stunden — Aktivzeit pro Stunde (blau) · Aktionen + Seitenaufrufe (Zahl)</p>
              <div className="flex items-end gap-[3px] h-20 bg-secondary/30 border border-border/60 rounded-sm p-2" data-testid="hourly-24h-chart">
                {hourly.map((h, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center justify-end h-full min-w-0"
                    title={`${h.label} — ${h.active_s ? fmtDur(h.active_s) : '0m'} aktiv · ${h.actions} Aktionen · ${h.views || 0} Aufrufe${h.logins ? ` · ${h.logins} Login` : ''}`}>
                    {(h.actions + (h.views || 0)) > 0 && <span className="text-[8px] font-mono text-amber-400 leading-none mb-0.5">{h.actions + (h.views || 0)}</span>}
                    <div className="w-full rounded-[1px] bg-blue-500"
                      style={{ height: `${h.active_s > 0 ? Math.max((h.active_s / maxActive) * 100, 6) : 2}%`, opacity: h.active_s > 0 ? 1 : 0.15 }} />
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-[9px] font-mono text-muted-foreground/60 mt-0.5 px-2">
                <span>{hourly[0]?.label}</span><span>{hourly[8]?.label}</span><span>{hourly[16]?.label}</span><span>{hourly[23]?.label}</span>
              </div>
            </div>
            <div className="flex-1 min-h-0 flex flex-col">
              <p className="text-[10px] text-muted-foreground/70 uppercase tracking-wider mb-1.5">Kompletter Log ({detail.log.length}{detail.has_more ? '+' : ''} Einträge)</p>
              <div className="flex-1 overflow-y-auto border border-border/60 rounded-sm divide-y divide-border/40" data-testid="user-activity-log">
                {detail.log.map((e, i) => (
                  <div key={i} className="flex items-start gap-2 px-2.5 py-1.5 text-xs">
                    <span className="font-mono text-muted-foreground/70 shrink-0 w-[86px]">{fmtTs(e.at)}</span>
                    {e.kind === 'login'
                      ? <LogIn className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" />
                      : e.kind === 'view'
                        ? <Eye className="w-3 h-3 text-muted-foreground mt-0.5 shrink-0" />
                        : <Zap className="w-3 h-3 text-blue-400 mt-0.5 shrink-0" />}
                    <span className={`min-w-0 ${e.kind === 'view' ? 'text-muted-foreground' : 'text-foreground/90'}`}>
                      {e.detail || e.action}
                      {e.project_name && <span className="text-muted-foreground"> · {e.project_name}</span>}
                    </span>
                  </div>
                ))}
                {detail.log.length === 0 && <p className="text-xs text-muted-foreground p-3">Keine Einträge.</p>}
              </div>
              {detail.has_more && (
                <button onClick={loadMore} disabled={loadingMore} data-testid="log-load-more-btn"
                  className="mt-2 text-[11px] font-mono px-3 py-1.5 rounded-sm border border-border text-muted-foreground hover:text-foreground hover:border-blue-500/40 transition-colors self-start">
                  {loadingMore ? 'Lädt…' : 'Mehr laden'}
                </button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function TeamAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [infoOpen, setInfoOpen] = useState(false);
  const [detailUser, setDetailUser] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try { const res = await api.getTeamAnalytics(); if (alive) setData(res.data); }
      catch { toast.error('Failed to load team analytics'); }
      finally { if (alive) setLoading(false); }
    };
    load();
    const iv = setInterval(load, 120000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><span className="w-6 h-6 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" /></div>;
  if (!data) return null;

  const reload = () => api.getTeamAnalytics().then(res => setData(res.data)).catch(() => {});

  const users = [...data.users].sort((a, b) =>
    (b.time_7d_s - a.time_7d_s) || (b.actions_7d - a.actions_7d) || ((b.last_login_at || '').localeCompare(a.last_login_at || '')));

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="team-analytics-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-foreground tracking-tight flex items-center gap-2.5">
          <BarChart3 className="w-6 h-6 text-blue-400" /> Team Analytics
          <button onClick={() => setInfoOpen(true)} data-testid="analytics-info-btn"
            className="text-muted-foreground/50 hover:text-blue-400 transition-colors" title="How these numbers are measured">
            <Info style={{ width: 18, height: 18 }} />
          </button>
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Who's active, how much, and on what — logins, time in app and deal activity per member.</p>
      </div>

      {/* Team totals — today */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <TotalCard icon={Users} label="Online now" value={data.totals.users_online_now} testId="total-online" />
        <TotalCard icon={Clock} label="Team time today" value={fmtDur(data.totals.active_today_seconds)} testId="total-time-today" />
        <TotalCard icon={Zap} label="Actions today" value={data.totals.actions_today} testId="total-actions-today" />
        <TotalCard icon={LogIn} label="Logins today" value={data.totals.logins_today} testId="total-logins-today" />
      </div>

      {/* Team totals — all time */}
      <div>
        <p className="text-[10px] text-muted-foreground/70 uppercase tracking-wider mb-2">Team · all time</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <TotalCard icon={Clock} label="Team time (all time)" value={fmtDur(data.totals.time_all_seconds)} testId="total-time-all" />
          <TotalCard icon={Zap} label="Actions (all time)" value={data.totals.actions_all} testId="total-actions-all" />
          <TotalCard icon={LogIn} label="Logins (all time)" value={data.totals.logins_all} testId="total-logins-all" />
          <TotalCard icon={Flame} label="Team active days" value={users.reduce((s, u) => s + (u.active_days_all || 0), 0)} testId="total-active-days" />
        </div>
      </div>

      {/* Per-user cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {users.map(u => <UserCard key={u.id} u={u} onOpen={setDetailUser} onReload={reload} allUsers={data.users} />)}
      </div>

      <UserDetailDialog user={detailUser} onClose={() => setDetailUser(null)}
        onResetDone={() => api.getTeamAnalytics().then(res => setData(res.data)).catch(() => {})} />

      <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="analytics-definitions">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-400" /> How these numbers are measured
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-1">
            <p className="text-xs text-muted-foreground leading-relaxed"><span className="text-foreground font-medium">Login</span> — counted only when someone completes the full sign-in (password + authenticator code). Sessions stay valid for 72 hours, so staying logged in does not create new login events — use "time in app" and the online dot for day-to-day activity.</p>
            <p className="text-xs text-muted-foreground leading-relaxed"><span className="text-foreground font-medium">Time in app</span> — accumulated while the tab or installed app is open and in the foreground (a heartbeat is sent every 60 seconds). Background tabs, closed apps or a locked phone do not count.</p>
            <p className="text-xs text-muted-foreground leading-relaxed"><span className="text-foreground font-medium">Actions</span> — entries from the deal activity log (claims, notes, edits, uploads, passes, meeting adds…). Action history covers all time; login &amp; time tracking started with v1.39{data.tracking_since ? ` (${data.tracking_since})` : ''} — "all time" for those two means since then.</p>
            <p className="text-xs text-muted-foreground leading-relaxed"><span className="text-foreground font-medium">Active days</span> — days on which a member spent at least one minute in the app (since tracking started).</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
